<template>
  <div class="cat">
    <!-- filters: date range + type chips, one quiet row -->
    <div class="cat-bar">
      <calendar-picker-range v-model="dateRange" class="cat-bar__range"/>
      <button type="button" class="cat-tool" v-b-tooltip.hover :title="$t('roomBoard.reload')" @click="refresh">
        <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
      </button>
    </div>
    <div class="cat-types">
      <button type="button" class="cat-tab" :class="{'cat-tab--on': !selectedTypes.length}" @click="selectedTypes = []">
        {{ $t('clientActivity.all') }}
      </button>
      <button v-for="type in allTypes" :key="type" type="button"
              class="cat-tab" :class="{'cat-tab--on': selectedTypes.includes(type)}" @click="toggleType(type)">
        <i :class="iconOf(type)" class="mr-25"/>{{ $t(`clientActivity.types.${type}`) }}
      </button>
    </div>

    <div :class="{'cat-scroll': scrollable}">
      <b-overlay :show="loading" rounded="sm" spinner-variant="primary">
        <app-timeline v-if="events.length" class="cat-timeline">
          <app-timeline-item v-for="(event, index) in events" :key="index" :variant="variantOf(event)">
            <div class="cat-item">
              <div v-if="dayLabelOf(index)" class="cat-day">{{ dayLabelOf(index) }}</div>
              <div class="cat-item__head">
                <span class="cat-item__title"><i :class="iconOf(event.type)" class="mr-25"/>{{ $t(`clientActivity.titles.${event.type}`) }}</span>
                <span class="cat-item__time tabular" v-b-tooltip.hover :title="event.time | moment('dddd, DD MMMM YYYY HH:mm')">
                  {{ event.time | moment('HH:mm') }} <span class="cat-item__date">· {{ event.time | moment('DD.MM.YYYY') }}</span>
                </span>
              </div>

              <!-- RESERVATION -->
              <div v-if="event.type === 'RESERVATION'" class="cat-item__body">
                <b>{{ event.data.propertyAddress || '—' }}</b>
                <span v-if="event.data.checkIn" class="tabular"> · {{ event.data.checkIn | moment('DD.MM.YY') }} → {{ event.data.checkOut | moment('DD.MM.YY') }}</span>
                <b-badge v-if="event.data.platform" variant="light-primary" class="ml-25">{{ event.data.platform }}</b-badge>
                <b-badge v-if="event.data.status" :variant="event.data.status === 'ACTIVE' ? 'light-success' : 'light-secondary'" class="ml-25">{{ event.data.status }}</b-badge>
                <span v-if="event.data.total" class="cat-money tabular ml-25">{{ event.data.total | numberFormatter }} €</span>
                <button type="button" class="cat-link ml-50" @click="$emit('open-contract', event.data.contractId)">
                  <i class="fa-solid fa-arrow-up-right-from-square"/>
                </button>
              </div>

              <!-- PAYMENT -->
              <div v-else-if="event.type === 'PAYMENT'" class="cat-item__body">
                <span class="cat-money tabular" :class="event.data.paid ? 'cat-money--ok' : 'cat-money--warn'">
                  {{ event.data.amount | numberFormatter }} {{ event.data.currency || '€' }}
                </span>
                <b-badge :variant="event.data.paid ? 'light-success' : 'light-warning'" class="ml-25">
                  {{ event.data.paid ? $t('clientActivity.paid') : $t('clientActivity.unpaid') }}
                </b-badge>
                <span v-if="event.data.reason" class="text-muted ml-25">{{ event.data.reason }}</span>
              </div>

              <!-- FEATURE -->
              <div v-else-if="event.type === 'FEATURE'" class="cat-item__body">
                <b>{{ event.data.name }}</b>
                <span v-if="event.data.price" class="cat-money tabular ml-25">{{ event.data.price | numberFormatter }} €</span>
                <b-badge v-if="event.data.status" variant="light-info" class="ml-25">{{ event.data.status }}</b-badge>
                <b-badge v-if="event.data.clientRequest" variant="light-success" class="ml-25">{{ $t('clientRequest') }}</b-badge>
              </div>

              <!-- JOURNEY -->
              <div v-else-if="event.type === 'JOURNEY'" class="cat-item__body">
                <b-badge variant="light-info">{{ event.data.status || '—' }}</b-badge>
              </div>

              <!-- CALL -->
              <div v-else-if="event.type === 'CALL'" class="cat-item__body">
                <b-badge :variant="callFailed(event) ? 'light-danger' : 'light-success'">
                  {{ event.data.incoming ? $t('clientActivity.incoming') : $t('clientActivity.outgoing') }}
                </b-badge>
                <span v-if="event.data.status" class="text-muted ml-25">{{ event.data.status }}</span>
                <span v-if="event.data.duration" class="text-muted tabular ml-25">{{ event.data.duration }}s</span>
              </div>

              <!-- SMS / WHATSAPP -->
              <div v-else-if="event.type === 'SMS' || event.type === 'WHATSAPP'" class="cat-item__body">
                <b-badge v-if="event.type === 'WHATSAPP'" :variant="event.data.incoming ? 'light-info' : 'light-success'" class="mr-25">
                  {{ event.data.incoming ? $t('clientActivity.incoming') : $t('clientActivity.outgoing') }}
                </b-badge>
                <span class="cat-text">{{ event.data.text }}</span>
              </div>

              <div v-else-if="event.description" class="cat-item__body text-muted">{{ event.description }}</div>
            </div>
          </app-timeline-item>
        </app-timeline>

        <div v-else-if="!loading" class="cat-empty">
          <i class="fa-solid fa-timeline"/>
          <p>{{ $t('clientActivity.empty') }}</p>
        </div>
      </b-overlay>

      <div v-if="page < pages" class="text-center mt-1">
        <b-button variant="outline-primary" size="sm" :disabled="loading" @click="loadMore">
          {{ $t('clientActivity.loadMore') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import CalendarPickerRange from '@/components/widgets/calendar/calendarPickerRange.vue'

const ALL_TYPES = ['CREATED', 'RESERVATION', 'PAYMENT', 'FEATURE', 'JOURNEY', 'CALL', 'SMS', 'WHATSAPP']
const PAGE_SIZE = 100

export default {
  name: 'ClientActivityTimeline',
  components: {AppTimeline, AppTimelineItem, CalendarPickerRange},
  props: {
    clientId: {type: Number, default: null},
    /** inside the contract sidebar the timeline scrolls in its own box */
    scrollable: {type: Boolean, default: false},
  },
  data: () => ({
    allTypes: ALL_TYPES,
    selectedTypes: [],
    dateRange: {start: null, end: null},
    events: [],
    page: 1,
    pages: 0,
    loading: false,
  }),
  watch: {
    clientId: {immediate: true, handler() { this.refresh() }},
    selectedTypes() { this.refresh() },
    dateRange: {deep: true, handler() { this.refresh() }},
  },
  methods: {
    ...mapActions('clientActivity', ['getActivity']),
    refresh() {
      if (!this.clientId) {
        this.events = []
        return
      }
      this.page = 1
      this.fetch(false)
    },
    loadMore() {
      this.page += 1
      this.fetch(true)
    },
    fetch(append) {
      this.loading = true
      this.getActivity({
        clientId: this.clientId,
        types: this.selectedTypes.length ? this.selectedTypes : null,
        from: (this.dateRange && this.dateRange.start) || null,
        until: (this.dateRange && this.dateRange.end) || null,
        page: this.page,
        size: PAGE_SIZE,
      }).then(data => {
        this.events = append ? this.events.concat(data.Events || []) : (data.Events || [])
        this.pages = data.Pages || 0
      }).catch(() => { /* the store already toasts the reason */ })
          .finally(() => { this.loading = false })
    },
    toggleType(type) {
      this.selectedTypes = this.selectedTypes.includes(type)
          ? this.selectedTypes.filter(t => t !== type)
          : [...this.selectedTypes, type]
    },
    dayLabelOf(index) {
      const event = this.events[index]
      if (!event) return null
      const day = this.$moment(event.time)
      if (index > 0 && day.isSame(this.$moment(this.events[index - 1].time), 'day')) return null
      if (day.isSame(this.$moment(), 'day')) return this.$t('clientActivity.today')
      if (day.isSame(this.$moment().subtract(1, 'day'), 'day')) return this.$t('clientActivity.yesterday')
      return day.format('dddd, DD MMM YYYY')
    },
    callFailed(event) {
      return ['failed', 'busy', 'no-answer', 'canceled'].includes(String(event.data.status || '').toLowerCase())
    },
    variantOf(event) {
      switch (event.type) {
        case 'CREATED': return 'success'
        case 'RESERVATION': return 'primary'
        case 'PAYMENT': return event.data && event.data.paid ? 'success' : 'warning'
        case 'FEATURE': return 'info'
        case 'JOURNEY': return 'info'
        case 'CALL': return this.callFailed(event) ? 'danger' : 'success'
        case 'SMS': return 'secondary'
        case 'WHATSAPP': return 'success'
        default: return 'secondary'
      }
    },
    iconOf(type) {
      switch (type) {
        case 'CREATED': return 'fa-solid fa-user-plus'
        case 'RESERVATION': return 'fa-solid fa-bed'
        case 'PAYMENT': return 'fa-solid fa-coins'
        case 'FEATURE': return 'fa-solid fa-gift'
        case 'JOURNEY': return 'fa-solid fa-route'
        case 'CALL': return 'fa-solid fa-phone'
        case 'SMS': return 'fa-solid fa-comment-sms'
        case 'WHATSAPP': return 'fa-brands fa-whatsapp'
        default: return 'fa-solid fa-circle-info'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

.cat-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.cat-bar__range { flex: 0 1 380px; min-width: 0; :deep(.cal-range-picker__row) { margin-bottom: 0; } }
.cat-tool {
  width: 30px; height: 30px; border: 1px solid rgba(23, 30, 48, .12); border-radius: 7px; cursor: pointer;
  background: transparent; color: #7c8598; font-size: .7rem; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { border-color: #3b6ff6; color: #3b6ff6; }
  &:focus { outline: none; }
}

.cat-types { display: flex; flex-wrap: wrap; gap: 2px; margin-bottom: 14px; }
.cat-tab {
  border: none; background: transparent; cursor: pointer;
  font-size: .72rem; font-weight: 700; color: #7c8598;
  padding: 6px 10px; border-radius: 6px; white-space: nowrap;
  &:hover { color: #171e30; background: rgba(23, 30, 48, .04); }
  &:focus { outline: none; }
  &--on { color: #171e30; box-shadow: inset 0 -2px 0 #3b6ff6; }
}

.cat-scroll { max-height: 56vh; overflow-y: auto; overscroll-behavior: contain; padding-right: 6px; }
.cat-timeline { padding-left: 4px; }

.cat-day {
  display: inline-block; font-size: .62rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase;
  color: #3b6ff6; margin-bottom: 5px;
}
.cat-item { padding-bottom: 4px; }
.cat-item__head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.cat-item__title { font-weight: 700; color: #171e30; font-size: .84rem; }
.cat-item__time { font-size: .72rem; font-weight: 700; color: #7c8598; white-space: nowrap; }
.cat-item__date { font-weight: 500; color: #9aa2b1; }
.cat-item__body { font-size: .8rem; margin-top: 3px; color: #4a5468; }
.cat-text { font-style: italic; color: #4a5468; }
.cat-money { font-weight: 800; color: #2a3348; &--ok { color: #2fa46a; } &--warn { color: #c07f14; } }
.cat-link {
  border: none; background: none; padding: 0; cursor: pointer; color: #3b6ff6; font-size: .68rem;
  &:hover { color: #2a56c8; }
  &:focus { outline: none; }
}
.cat-empty {
  text-align: center; color: #9aa2b1; padding: 40px 0;
  i { font-size: 1.8rem; opacity: .35; }
  p { margin-top: 8px; font-size: .82rem; }
}
</style>

<style lang="scss">
/* the extra .cat ancestor outguns the scoped light-mode rules, which tie on specificity otherwise */
.dark-layout .cat {
  .cat-item__title { color: #eef1f7; }
  .cat-item__body { color: #d7dde9; b { color: #ffffff; } }
  .cat-item__body .text-muted { color: #a3b0c6 !important; }
  .cat-item__time { color: #9aa8bf; }
  .cat-item__date { color: #8a93a5; }
  .cat-text { color: #e4e9f2; font-style: normal; }
  .cat-day { color: #7ba3ff; }
  .cat-money { color: #ffffff; &--ok { color: #4ecb8d; } &--warn { color: #e6b05a; } }
  .cat-link { color: #7ba3ff; }
  .cat-tab { color: #8a93a5; &:hover { color: #eef1f7; background: rgba(255, 255, 255, .05); } }
  .cat-tab--on { color: #eef1f7; box-shadow: inset 0 -2px 0 #5b8bff; }
  .cat-tool { border-color: rgba(255, 255, 255, .14); }
}
</style>
